import classNames from "classnames";
import { styled } from "@linaria/react";
import React from "react";
import Checkbox from "../Checkbox";
import Link from "../Link";
import { color } from "..";
import { fonts, spacings } from "../Theme";
import { css } from "@linaria/core";

// exported for keyboard navigation between options
export const KEYBOARD_ACTIVE_CN = "keyboard-active";
export const KEYBOARD_ACTIVABLE_CN = "keyboard-activable";
export const DISABLED_CN = "disabled";
export const SELECTED_CN = "selected";
export const CHECKBOX_CN = "checkbox";
export const CLOSE_ON_CLICK_CN = "close-on-click";
export const REMOVED = "removed";

// interface P {
//   checkbox?: boolean;
//   selected?: boolean;
//   disabled?: boolean;
//   closeOnClick?: boolean;
//   removed?: boolean;
//   to?: string;
//   exact?: boolean;
//   hash?: string;
//   active?: boolean;
// }

type Props = {
  checkbox?: boolean;
  selected?: boolean;
  disabled?: boolean;
  closeOnClick?: boolean;
  removed?: boolean;
  to?: string;
  exact?: boolean;
  hash?: string;
  active?: boolean;
  className?: string;
  href?: React.AnchorHTMLAttributes<HTMLAnchorElement>["href"];
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>["target"];
  tabIndex?: number;
  onClick?: (e: React.MouseEvent) => void;
} & React.HTMLAttributes<HTMLAnchorElement | HTMLDivElement>;

const OptionContainer = css`
  white-space: nowrap;
  outline: none;
  display: flex;
  align-items: center;
  height: 36px;
  gap: ${spacings.xs};
  /* padding: 0 ${spacings.xxs}; */
  padding: ${spacings.xs};
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  flex: none;
  font: ${fonts.body2};
  color: ${color.carbon900};

  &:has(*) {
    height: 40px;
  }

  & > svg {
    fill: ${color.carbon800};
    flex-shrink: 0;
    margin-right: ${spacings.xxs};
  }

  &.${KEYBOARD_ACTIVE_CN}, &:not(.${DISABLED_CN}):hover {
    background-color: ${color.titanium100};
  }

  &.${DISABLED_CN} {
    cursor: not-allowed;
    color: rgba(17, 24, 31, 0.5);
    > *:not(.${CHECKBOX_CN}) {
      opacity: 0.5;
    }
  }

  &:not(.${CHECKBOX_CN}).${SELECTED_CN} {
    color: ${color.carbon900};
    background-color: ${color.lightNitrogen50};
  }

  &.${REMOVED} {
    display: none;
  }
`;

const OptionCheckbox = css`
  width: auto;
  margin-right: 4px;
`;

const Option = ({
  className,
  onClick,
  closeOnClick,
  disabled,
  children,
  selected,
  checkbox,
  removed,
  ...props
}: React.PropsWithChildren<Props>) => {
  const Container =
    typeof props.href === "string" && props.href.length > 0
      ? "a"
      : typeof props.to === "string" && props.to.length > 0
        ? Link
        : "div";
  return (
    <Container
      title={typeof children === "string" ? children : ""}
      onClick={disabled ? undefined : onClick}
      className={classNames(OptionContainer, className, {
        [CHECKBOX_CN]: checkbox,
        [KEYBOARD_ACTIVABLE_CN]: !disabled,
        [DISABLED_CN]: disabled,
        [SELECTED_CN]: selected,
        [CLOSE_ON_CLICK_CN]: closeOnClick,
        [REMOVED]: removed,
      })}
      {...props}
    >
      {checkbox && (
        <Checkbox
          tabIndex={-1}
          readOnly
          checked={selected}
          disabled={disabled}
          smallIndicator
          className={OptionCheckbox}
        />
      )}
      {children}
    </Container>
  );
};

const GroupTitle = styled.div`
  font: ${fonts.body2SemiBold};
  color: ${color.carbon700};
  margin: 8px;
`;

const GroupWrapper = styled.div`
  width: 100%;

  :not(:first-child) {
    padding-top: 4px;
  }

  :not(:last-child) {
    border-bottom: 1px solid;
    border-bottom-color: ${color.titanium100};

    padding-bottom: 4px;
  }
`;

interface OptionGroupProps extends Omit<React.HtmlHTMLAttributes<HTMLDivElement>, "title"> {
  title?: React.ReactNode;
  children?: React.ReactNode;
}

const OptionGroup = ({ title, children, ...props }: OptionGroupProps) => (
  <GroupWrapper {...props}>
    {title && <GroupTitle>{title}</GroupTitle>}
    {children}
  </GroupWrapper>
);

export { OptionGroup };
export default React.memo(Option);
